import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import axios from 'axios';
import Footer from './Footer';
import SEO from './SEO';
import './Login.css';
import { needsProfileCompletion } from "../utils/profileCompletion";
import { getMyDirectoryListing } from "../services/directoryService";
import { fireCheckoutIntent } from "../utils/checkoutAnalytics";

/**
 * Where each "no account here" answer sends the visitor.
 *
 * Both messages end in "... here.", so the word "here" is turned into the
 * link. Appending a second call to action after the sentence read as
 * "You can start a free trial here. Start a free trial."
 */
const ACCOUNT_ERROR_LINKS = {
  NO_LAWFUSION_ACCOUNT: '/pricing',
  NO_DIRECTORY_ACCOUNT: '/directory?join=1',
};

function renderAccountError(message, code) {
  const to = ACCOUNT_ERROR_LINKS[code];
  if (!to || !message) return message;

  // Link the final "here"; if the copy ever loses it, append the link instead
  // of silently dropping the way out.
  const at = message.lastIndexOf('here');
  if (at === -1) {
    return (
      <>
        {message}{' '}
        <Link to={to} style={{ color: 'inherit', fontWeight: 700 }}>
          Continue
        </Link>
        .
      </>
    );
  }
  return (
    <>
      {message.slice(0, at)}
      <Link to={to} style={{ color: 'inherit', fontWeight: 700 }}>
        here
      </Link>
      {message.slice(at + 4)}
    </>
  );
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [errorCode, setErrorCode] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleInitialized, setGoogleInitialized] = useState(false);
  const { login, signInWithGoogle, isAuthenticated, loading: authLoading, user } = useAuth();
  const { confirm } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  // Normalize URL - remove trailing slash to ensure consistency for OAuth origins
  useEffect(() => {
    if (location.pathname !== '/login' && location.pathname.endsWith('/')) {
      const normalizedPath = location.pathname.replace(/\/+$/, '');
      navigate(normalizedPath + location.search, { replace: true });
    }
  }, [location, navigate]);

  // Which side of the product the visitor picked in the login dropdown. Both
  // options share this form and the same credentials; the choice only decides
  // where they land afterwards.
  // Seeded from the nav ("Log in to Directory" adds ?area=directory) and then
  // owned by the radio group below, so someone who lands here directly — or
  // arrives with one intent and changes their mind — can switch without going
  // back to the menu. Default is LawFusion.
  const [loginArea, setLoginArea] = React.useState(() =>
    new URLSearchParams(location.search).get("area") === "directory"
      ? "directory"
      : "lawfusion",
  );
  const isDirectoryLogin = loginArea === "directory";

  const fromState = location.state?.from;
  const redirectPath = typeof fromState === 'string' ? fromState : fromState?.pathname || '/research';
  const googleButtonRef = React.useRef(null);
  const postAuthRedirectStarted = React.useRef(false);

  /**
   * A directory login goes to the listing the person came to manage. Someone
   * with a LawFusion account but no listing yet is sent to the directory setup
   * form rather than shown an error.
   */
  const redirectAfterDirectoryLogin = React.useCallback(async () => {
    if (postAuthRedirectStarted.current) {
      return;
    }
    postAuthRedirectStarted.current = true;

    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const listing = await getMyDirectoryListing(authHeaders);
      navigate(listing ? "/my-listing" : "/directory?join=1", { replace: true });
    } catch (err) {
      console.error("Error loading directory listing after login:", err);
      navigate("/directory?join=1", { replace: true });
    }
  }, [navigate]);

  const checkAndRedirectToPricing = React.useCallback(async () => {
    if (postAuthRedirectStarted.current) {
      return;
    }
    postAuthRedirectStarted.current = true;

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      const config = {
        withCredentials: true
      };
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }

      // If a pending plan was selected on Pricing before login, go straight to checkout
      const pendingPlan = localStorage.getItem("pendingPlan");
      if (pendingPlan) {
        try {
          const plan = JSON.parse(pendingPlan);
          localStorage.removeItem("pendingPlan");
          const event_id = await fireCheckoutIntent(plan, null);
          const res = await axios.post(`${API_URL}/stripe/create-checkout-session`, {
            planName: plan.name.toLowerCase(),
            price: plan.price,
            event_id,
          }, config);
          if (res.data?.url) {
            window.location.href = res.data.url;
            return;
          }
        } catch (err) {
          console.error("Failed to start pending checkout:", err);
          localStorage.removeItem("pendingPlan");
        }
      }

      // Fetch subscription status
      const subscriptionResponse = await axios.get(`${API_URL}/payments/subscription-status`, config);
      const subscription = subscriptionResponse.data;

      // Check if user is first-time (no subscription, no trial, no usage allotted)
      const hasActiveSubscription = subscription.subscriptionStatus === 'active' || subscription.subscriptionStatus === 'trialing';
      const hasUsageAllotted =
        (subscription.aiMonthlyLimit || 0) > 0 ||
        (subscription.subscriptionActionsLimit || 0) > 0 ||
        (user?.aiMonthlyLimit || 0) > 0 ||
        (user?.subscriptionActionsLimit || 0) > 0;
      const hasAdminAllocatedActions =
        (subscription.adminAllocatedActionsTotal || 0) > 0 ||
        (user?.adminAllocatedActionsTotal || 0) > 0;
      const isFirstTimeUser =
        !hasActiveSubscription && !hasUsageAllotted && !hasAdminAllocatedActions;

      if (isFirstTimeUser) {
        // Redirect to pricing page
        navigate('/pricing', { replace: true });
      } else if (needsProfileCompletion(user)) {
        navigate("/complete-profile", { replace: true });
      } else {
        // Redirect to original destination or dashboard
        navigate(redirectPath, { replace: true });
      }
    } catch (error) {
      console.error('Error checking subscription status after login:', error);
      // On error, proceed to original destination
      navigate(redirectPath, { replace: true });
    }
  }, [navigate, redirectPath, user]);

  useEffect(() => {
    if (!isAuthenticated) {
      postAuthRedirectStarted.current = false;
    }
  }, [isAuthenticated]);

  const handleGoogleCallback = React.useCallback(async (response) => {
    if (!response.credential) {
      setError('Google sign-in failed. Please try again.');
      setGoogleLoading(false);
      userInitiatedGoogleAction.current = false;
      return;
    }

    setGoogleLoading(true);
    setError(''); // Clear any previous errors

    const result = await signInWithGoogle(response.credential, 'signin');
    setGoogleLoading(false);
    userInitiatedGoogleAction.current = false; // Reset flag after callback

    if (result.success) {
      setError(''); // Clear errors on success
      if (isDirectoryLogin) {
        await redirectAfterDirectoryLogin();
      } else {
        // Check subscription status and redirect accordingly
        await checkAndRedirectToPricing();
      }
    } else {
      if (result.message?.includes('Please sign up first')) {
        const shouldGoToSignup = await confirm({
          title: 'Account Not Found',
          message: result.message,
          confirmText: 'Go to Sign Up',
          cancelText: 'Close',
          type: 'warning',
        });
        if (shouldGoToSignup) {
          navigate('/signup');
          return;
        }
      }
      // Show all error messages to the user
      if (result.message) {
        setError(result.message);
      } else {
        setError('Google sign-in failed. Please try again.');
      }
    }
  }, [
    signInWithGoogle,
    checkAndRedirectToPricing,
    confirm,
    navigate,
    isDirectoryLogin,
    redirectAfterDirectoryLogin,
  ]);

  useEffect(() => {
    setError('');
    setFieldErrors({ email: '', password: '' });
    setTouched({ email: false, password: false });
    
    const urlParams = new URLSearchParams(location.search);
    const verified = urlParams.get('verified');
    const emailParam = urlParams.get('email');
    const errorParam = urlParams.get('error');
    
    if (verified === 'true' && emailParam) {
      setEmail(emailParam);
      setError('');
      setSuccessMessage('Email verified successfully! Your account has been created. Please login with your credentials.');
    } else if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setSuccessMessage('');
    }
  }, [location.search]);

  // Validation functions
  const validateEmail = (emailValue) => {
    if (!emailValue) {
      return 'Email address is required';
    }
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(emailValue)) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }
    return '';
  };

  const validatePassword = (passwordValue) => {
    if (!passwordValue) {
      return 'Password is required';
    }
    if (passwordValue.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return '';
  };

  // Handle field blur
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'email') {
      const error = validateEmail(email);
      setFieldErrors(prev => ({ ...prev, email: error }));
    } else if (field === 'password') {
      const error = validatePassword(password);
      setFieldErrors(prev => ({ ...prev, password: error }));
    }
  };

  // Handle field change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(value) }));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) {
      setFieldErrors(prev => ({ ...prev, password: validatePassword(value) }));
    }
  };

  // Track user-initiated Google login actions
  const userInitiatedGoogleAction = React.useRef(false);

  // Handle Google sign-in errors - only for actual user-facing errors
  useEffect(() => {
    const handleGoogleError = (error) => {
      setGoogleLoading(false);

      // Don't show errors for user cancellations
      if (error.type === 'popup_closed_by_user' || error.type === 'user_cancelled') {
        userInitiatedGoogleAction.current = false;
        return;
      }

      // Only show errors if user actually tried to use Google sign-in
      if (!userInitiatedGoogleAction.current) {
        return;
      }

      const errorMessage = error.message || error.detail?.message || error.error || '';

      // Only show critical errors that prevent functionality
      if (errorMessage.includes('popup_blocked')) {
        setError('Google Sign-In Error: Popup was blocked. Please allow popups for this site and try again.');
      } else if (errorMessage.includes('access_denied')) {
        // User denied access, don't show error
        userInitiatedGoogleAction.current = false;
        return;
      } else if (errorMessage && !errorMessage.includes('invalid_client') && !errorMessage.includes('401') && !errorMessage.includes('no registered origin')) {
        // Only show non-configuration errors for user actions
        setError(`Google Sign-In Error: ${errorMessage}`);
      }

      // Reset flag after handling
      userInitiatedGoogleAction.current = false;
    };

    // Listen for Google errors from the library - only handle actual error events
    const errorHandler = (e) => {
      // Only handle if it's a real error event, not console warnings
      if (e.type === 'error' || e.detail) {
        handleGoogleError(e.detail || e);
      }
    };

    window.addEventListener('google-signin-error', errorHandler);

    return () => {
      window.removeEventListener('google-signin-error', errorHandler);
    };
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      setError(''); // Clear any errors when authenticated
      if (isDirectoryLogin) {
        redirectAfterDirectoryLogin();
      } else {
        // Check if user should be redirected to pricing (first-time user)
        checkAndRedirectToPricing();
      }
    }
  }, [
    isAuthenticated,
    authLoading,
    checkAndRedirectToPricing,
    isDirectoryLogin,
    redirectAfterDirectoryLogin,
  ]);

  // Load Google Identity Services script
  useEffect(() => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.warn('Google Client ID not configured. Google sign-in will not work.');
      return;
    }

    // Check if script is already loaded
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      // Script already loaded, just initialize
      if (window.google && window.google.accounts) {
        try {
          const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
          if (!clientId) {
            // Don't show error on initialization - only when user tries to use it
            return;
          }

          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleCallback,
            auto_select: false,
            cancel_on_tap_outside: true,
            itp_support: true, // Enabled ITP support for better Incognito/Safari compatibility
          });

          // Render the hidden Google button
          if (window.google.accounts.id && googleButtonRef.current) {
            try {
              window.google.accounts.id.renderButton(googleButtonRef.current, {
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                width: 300,
              });
              setGoogleInitialized(true);
              setError(''); // Clear any errors if initialization succeeds
            } catch (error) {
              console.error('Error rendering Google button:', error);
              // Don't show error if it's just a configuration warning and button might still work
              // Only log to console, don't show to user unless they try to use it
            }
          }
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
          // Don't show initialization errors to user - they might be false positives
          // Only show errors when user actually tries to use Google sign-in
        }
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.accounts) {
        try {
          const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
          if (!clientId) {
            // Don't show error on initialization - only when user tries to use it
            return;
          }

          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleCallback,
            auto_select: false,
            cancel_on_tap_outside: true,
            itp_support: true, // Enabled ITP support for better Incognito/Safari compatibility
          });

          // Render the hidden Google button
          if (window.google.accounts.id && googleButtonRef.current) {
            try {
              window.google.accounts.id.renderButton(googleButtonRef.current, {
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                width: 300,
              });
              setGoogleInitialized(true);
              setError(''); // Clear any errors if initialization succeeds
            } catch (error) {
              console.error('Error rendering Google button:', error);
              // Don't show error if it's just a configuration warning and button might still work
              // Only log to console, don't show to user unless they try to use it
            }
          }
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
          // Don't show initialization errors to user - they might be false positives
          // Only show errors when user actually tries to use Google sign-in
        }
      }
    };
    script.onerror = () => {
      console.error('Failed to load Google Identity Services script');
    };
    document.body.appendChild(script);
  }, [handleGoogleCallback]);

  if (authLoading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode('');

    // Mark all fields as touched
    setTouched({ email: true, password: true });

    // Validate all fields
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    setFieldErrors({
      email: emailError,
      password: passwordError
    });

    // If there are validation errors, don't submit
    if (emailError || passwordError) {
      return;
    }

    setLoading(true);

    const result = await login(email, password, loginArea);
    setLoading(false);

    if (result.success) {
      if (isDirectoryLogin) {
        await redirectAfterDirectoryLogin();
      } else {
        // Check subscription status and redirect accordingly
        await checkAndRedirectToPricing();
      }
    } else {
      // Handle backend errors
      const backendError = result.message || 'Login failed. Please try again.';
      setError(backendError);
      setErrorCode(result.code || '');

      // A coded "no account here" answer is about the account, not a malformed
      // field, and it carries a link. Repeating it under the email input
      // printed the same sentence twice and stranded the link in the copy that
      // did not have one.
      const isAccountLevel =
        result.code === 'NO_LAWFUSION_ACCOUNT' ||
        result.code === 'NO_DIRECTORY_ACCOUNT';

      // If it's a specific field error, show it in the field
      if (isAccountLevel) {
        // handled by the banner above
      } else if (backendError.toLowerCase().includes('email')) {
        setFieldErrors(prev => ({ ...prev, email: backendError }));
      } else if (backendError.toLowerCase().includes('password')) {
        setFieldErrors(prev => ({ ...prev, password: backendError }));
      }
    }
  };

  const handleGoogleLogin = () => {
    setError(''); // Clear any previous errors
    setGoogleLoading(true);
    userInitiatedGoogleAction.current = true; // Mark that user initiated action

    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      setError('Google Sign-In is not configured. Please contact support.');
      setGoogleLoading(false);
      userInitiatedGoogleAction.current = false;
      return;
    }

    if (window.google && window.google.accounts && window.google.accounts.id) {
      try {
        // Find the hidden Google button and click it programmatically
        const googleButton = document.getElementById('google-signin-button-hidden');
        if (!googleButton) {
          setError('Google Sign-In is not available. Please refresh the page.');
          setGoogleLoading(false);
          return;
        }

        // Try to find and click the button, with retries
        const tryClickButton = (attempts = 0) => {
          const buttonElement = googleButton.querySelector('div[role="button"]');
          if (buttonElement) {
            buttonElement.click();
            // Don't set loading to false here - it will be set in the callback
          } else if (attempts < 10) {
            // Retry up to 10 times (1 second total)
            setTimeout(() => tryClickButton(attempts + 1), 100);
          } else {
            // If button still not found, try to render it now
            if (window.google.accounts.id && googleButtonRef.current) {
              try {
                window.google.accounts.id.renderButton(googleButtonRef.current, {
                  theme: 'outline',
                  size: 'large',
                  text: 'signin_with',
                  width: 300,
                });
                setGoogleInitialized(true);
                // Wait a bit for rendering, then try clicking
                setTimeout(() => {
                  const btn = googleButton.querySelector('div[role="button"]');
                  if (btn) {
                    btn.click();
                  } else {
                    setError('Google Sign-In button is not ready. Please refresh the page.');
                    setGoogleLoading(false);
                  }
                }, 200);
              } catch (error) {
                console.error('Error rendering Google button:', error);
                setError('Failed to initialize Google Sign-In. Please try again.');
                setGoogleLoading(false);
              }
            } else {
              setError('Google Sign-In button is not ready. Please refresh the page.');
              setGoogleLoading(false);
            }
          }
        };

        tryClickButton();
      } catch (error) {
        console.error('Error triggering Google Sign-In:', error);
        setError('Failed to initialize Google Sign-In. Please try again.');
        setGoogleLoading(false);
      }
    } else {
      setError('Google Sign-In is not available. Please refresh the page and make sure Google Client ID is configured.');
      setGoogleLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      className="login-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <SEO title="Sign In" path="/login" description="Sign in to your LawFusion account to access AI-powered legal analysis, multi-model debate, and research tools." noIndex />
      <motion.div
        className="login-container"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.div
          className="login-card"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
          whileHover={{ boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)" }}
        >
          {/* Logo */}
          <motion.div
            className="logo-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Link to="/" style={{ display: 'inline-block', cursor: 'pointer' }}>
              <motion.img
                src="/logo.png"
                alt="LawFusion.AI Logo"
                className="login-logo"
                variants={itemVariants}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </Link>
            <motion.h1
              className="welcome-title"
              variants={itemVariants}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            >
              {isDirectoryLogin ? 'Directory Login' : 'Welcome Back'}
            </motion.h1>
            <motion.p
              className="welcome-tagline"
              variants={itemVariants}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              {isDirectoryLogin
                ? 'Manage your attorney directory listing.'
                : 'Multiple AIs. Stronger Arguments.'}
            </motion.p>
          </motion.div>

          {/* Login Form */}
          <motion.form
            onSubmit={handleSubmit}
            className="login-form"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div className="login-area-choice" variants={itemVariants}>
              <span className="login-area-legend" id="login-area-legend">
                Where would you like to log in?
              </span>
              <div
                className="login-area-options"
                role="radiogroup"
                aria-labelledby="login-area-legend"
              >
                {[
                  {
                    key: "lawfusion",
                    label: "Log in to LawFusion",
                    hint: "Access Research & Debate",
                  },
                  {
                    key: "directory",
                    label: "Log in to Directory",
                    hint: "Manage your listing",
                  },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={loginArea === opt.key}
                    className={`login-area-option${
                      loginArea === opt.key ? " is-selected" : ""
                    }`}
                    onClick={() => setLoginArea(opt.key)}
                  >
                    <span className="login-area-dot" aria-hidden="true" />
                    <span>
                      <span className="login-area-label">{opt.label}</span>
                      <span className="login-area-hint">{opt.hint}</span>
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="form-group"
              variants={itemVariants}
            >
              <motion.label
                htmlFor="email"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                Email Address
              </motion.label>
              <motion.input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={() => handleBlur('email')}
                required
                placeholder="you@firm.com"
                className={touched.email && fieldErrors.email ? 'input-error' : ''}
                whileFocus={{
                  scale: 1.02,
                  boxShadow: "0 0 0 3px rgba(66, 133, 244, 0.1)",
                  borderColor: touched.email && fieldErrors.email ? "#dc3545" : "#4285f4"
                }}
                transition={{ duration: 0.2 }}
              />
              {touched.email && fieldErrors.email && (
                <motion.div
                  className="field-error"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {fieldErrors.email}
                </motion.div>
              )}
            </motion.div>

            <motion.div
              className="form-group"
              variants={itemVariants}
            >
              <motion.label
                htmlFor="password"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                Password
              </motion.label>
              <div className="password-input-wrapper">
                <motion.input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => handleBlur('password')}
                  required
                  placeholder="Enter your password"
                  minLength="6"
                  className={touched.password && fieldErrors.password ? 'input-error' : ''}
                  whileFocus={{
                    scale: 1.02,
                    boxShadow: "0 0 0 3px rgba(66, 133, 244, 0.1)",
                    borderColor: touched.password && fieldErrors.password ? "#dc3545" : "#4285f4"
                  }}
                  transition={{ duration: 0.2 }}
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.password && fieldErrors.password && (
                <motion.div
                  className="field-error"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {fieldErrors.password}
                </motion.div>
              )}
            </motion.div>

            {/* Forgot Password Link */}
            <motion.div
              className="forgot-password"
              variants={itemVariants}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/forgot-password">Forgot password?</Link>
              </motion.div>
            </motion.div>

            <AnimatePresence>
              {successMessage && (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, y: -10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  style={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: 'white',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    textAlign: 'center',
                    fontWeight: '500',
                  }}
                >
                  {successMessage}
                </motion.div>
              )}
              {error && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, y: -10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {renderAccountError(error, errorCode)}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Login Button */}
            <motion.button
              type="submit"
              className="login-button"
              disabled={loading}
              variants={itemVariants}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 8px 20px rgba(66, 133, 244, 0.4)",
                y: -2
              }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              {loading ? (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ repeat: Infinity, duration: 1 }}
                >
                  Logging in...
                </motion.span>
              ) : (
                'Login'
              )}
            </motion.button>
          </motion.form>

          {/* Hidden Google Button Container */}
          <div
            id="google-signin-button-hidden"
            ref={googleButtonRef}
            style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
          />

          {/* Separator */}
          <motion.div
            className="separator"
            variants={itemVariants}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8 }}
          >
            <motion.span
              className="separator-line"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ delay: 0.9, duration: 0.5 }}
            />
            <motion.span
              className="separator-text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              or
            </motion.span>
            <motion.span
              className="separator-line"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ delay: 0.9, duration: 0.5 }}
            />
          </motion.div>

          {/* Google Login Button */}
          <motion.button
            type="button"
            className="google-button"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            variants={itemVariants}
            whileHover={{
              scale: 1.02,
              y: -2,
              boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)"
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <motion.svg
              className="google-icon"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
            >
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.037-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853" />
              <path d="M3.963 10.707c-.18-.54-.282-1.117-.282-1.707 0-.59.102-1.167.282-1.707V4.951H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.049l3.006-2.342z" fill="#FBBC05" />
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.951L3.963 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
            </motion.svg>
            {googleLoading ? 'Signing in...' : 'Continue With Google'}
          </motion.button>

          {/* Sign Up Link */}
          <motion.div
            className="signup-link"
            variants={itemVariants}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            Don't have an account?{' '}
            <motion.span
              style={{ display: "inline-block" }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/signup">Sign up</Link>
            </motion.span>
          </motion.div>

          <Footer variant="links-only" />

          {/* Copyright */}
          <motion.div
            className="copyright inside-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
          >
            © 2026 LawFusion.AI. All rights reserved
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Login;

